import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { v4 } from 'uuid';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import People from '../Icons/People';
import Button from '../Button';
import { getTotalChildCount } from './Counter';
import testIds from './testIds';
import tailwindConfig from '../../../../tailwind.config';
import type { RoomAndGuestCount } from '../../booking/meal-plan/types';
import useIsAboveBreakpoint from '../../../core/hooks/useIsAboveBreakpoint';
import GuestsOutline from '../Icons/GuestsOutline';
import SkipTranslationText from '../SkipTranslationText';

const Modal = dynamic(() => import('../Modal/Modal'));
const Counter = dynamic(() => import('./Counter'));
const ChildCounterAccordion = dynamic(() => import('./ChildCounterAccordion'));

type Props = {
  roomAndGuestCount: RoomAndGuestCount;
  setRoomAndGuestCount: (guestCount: RoomAndGuestCount) => void;
  roundedSearchBar?: boolean;
};

const GuestCounter = ({ roomAndGuestCount, setRoomAndGuestCount, roundedSearchBar }: Props) => {
  const { t } = useTranslation();
  const isAboveMD = useIsAboveBreakpoint('MD');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const [id] = useState(v4());
  const [tempGuestCount, setTempGuestCount] = useState(roomAndGuestCount);

  const setCountTempGuestCount = (operation: 'add' | 'remove', key: keyof RoomAndGuestCount) => {
    const count = {
      ...tempGuestCount,
      [key]: operation === 'add' ? tempGuestCount[key] + 1 : tempGuestCount[key] - 1,
    };
    setTempGuestCount(count);
    setRoomAndGuestCount(count);
  };

  useEffect(() => {
    if (isAboveMD) {
      setIsModalOpen(false);
    } else {
      setIsHidden(true);
    }
  }, [isAboveMD, setIsModalOpen]);

  useEffect(() => {
    setTempGuestCount(roomAndGuestCount);
  }, [roomAndGuestCount]);

  const events = {
    onToggle: () => {
      setRoomAndGuestCount(tempGuestCount);
      if (isAboveMD) {
        setIsHidden(!isHidden);
      } else {
        setIsModalOpen(!isModalOpen);
      }
    },
    onCloseDropdown: (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsHidden(true);
      }
    },
  };

  useEffect(() => {
    document.addEventListener('mousedown', events.onCloseDropdown);
    return () => {
      document.removeEventListener('mousedown', events.onCloseDropdown);
    };
  });

  const renderCounter = () => {
    return (
      <div
        className={classNames('p-5', {
          'p-6': isAboveMD && roundedSearchBar,
        })}
      >
        <div
          className={classNames('flex flex-col', {
            'gap-16': isAboveMD && roundedSearchBar,
          })}
        >
          <div className="flex flex-col">
            <div
              className={classNames(
                'flex justify-between items-center border-b-2 border-gray-200 border-dotted pb-4',
                { 'border-b-0': isAboveMD && roundedSearchBar }
              )}
            >
              <p
                className={classNames('text-small font-bold', {
                  'text-regular font-normal': isAboveMD && roundedSearchBar,
                })}
              >
                {t('hotel:availability.guest_counter.room')}
              </p>
              <Counter
                count={tempGuestCount}
                guestType="roomCount"
                setCount={setCountTempGuestCount}
                roundedSearchBar={isAboveMD && roundedSearchBar}
              />
            </div>
            <div
              className={classNames(
                'flex justify-between items-center border-b-2 border-gray-200 border-dotted py-4',
                { 'border-b-0': isAboveMD && roundedSearchBar }
              )}
            >
              <p
                className={classNames('text-small font-bold', {
                  'text-regular font-normal': isAboveMD && roundedSearchBar,
                })}
              >
                {t('hotel:availability.guest_counter.adult')}
              </p>
              <Counter
                count={tempGuestCount}
                guestType="adultCount"
                setCount={setCountTempGuestCount}
                roundedSearchBar={isAboveMD && roundedSearchBar}
              />
            </div>
            {isAboveMD && roundedSearchBar && (
              <div className="flex items-center justify-between pt-4">
                <p className="text-regular">
                  {t('component:child_counter_accordion.elementary_school_child')}
                </p>
                <Counter
                  roundedSearchBar
                  count={tempGuestCount}
                  guestType="seniorElementarySchoolChildCount"
                  setCount={setCountTempGuestCount}
                />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-8">
            <ChildCounterAccordion
              count={tempGuestCount}
              setCount={setCountTempGuestCount}
              roundedSearchBar={roundedSearchBar}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderFooter = () => {
    return (
      <div className="flex items-center justify-between border-t border-solid border-gray-200 px-5 py-3">
        <div className="flex flex-col gap-1">
          <p data-testid={testIds.footerTotalRoomCount} className="text-small font-bold">
            <SkipTranslationText
              i18nKey="hotel:availability.guest_counter.total_room_count"
              values={{
                roomCount: tempGuestCount.roomCount,
              }}
            />
          </p>
          <p data-testid={testIds.footerTotalGuestCount} className="text-mini text-gray-600">
            <SkipTranslationText
              i18nKey="hotel:availability.guest_counter.total_guest_count"
              values={{
                adultGuestCount: tempGuestCount.adultCount * tempGuestCount.roomCount,
                childGuestCount: getTotalChildCount(tempGuestCount) * tempGuestCount.roomCount,
              }}
            />
          </p>
        </div>
        {!isAboveMD && (
          <Button id="apply-button" size="small" color="red" onClick={events.onToggle}>
            {t('common:button.apply')}
          </Button>
        )}
      </div>
    );
  };

  return (
    <div
      className={classNames('relative h-12 lg:h-14', {
        'h-14': roundedSearchBar,
      })}
    >
      <button
        id={`counter-field-${id}`}
        type="button"
        className={classNames(
          'flex items-center px-5 w-full h-full text-left bg-gray-50 border border-gray-200 hover:border-gray-300 cursor-pointer',
          {
            'gap-2 rounded-full bg-white border-0': roundedSearchBar,
            rounded: !roundedSearchBar,
          }
        )}
        onClick={events.onToggle}
      >
        {roundedSearchBar ? (
          <GuestsOutline size={24} color={tailwindConfig.theme.colors.orange[700]} />
        ) : (
          isAboveMD && <People size={24} color={tailwindConfig.theme.colors.orange[700]} />
        )}
        <p
          data-testid={testIds.totalCount}
          className={classNames('truncate', { 'px-3': isAboveMD && !roundedSearchBar })}
        >
          <SkipTranslationText
            i18nKey="hotel:availability.guest_counter.total_room_and_guest_count"
            values={{
              adultGuestCount: tempGuestCount.adultCount * tempGuestCount.roomCount,
              childGuestCount: getTotalChildCount(tempGuestCount) * tempGuestCount.roomCount,
              roomCount: tempGuestCount.roomCount,
            }}
          />
        </p>
      </button>
      <div
        id={`counter-modal-${id}`}
        ref={ref}
        className={classNames(
          'absolute top-14 left-0 z-10 w-full bg-white rounded drop-shadow will-change-filter sm:w-[400px] lg:w-[320px]',
          {
            'top-18': isAboveMD && roundedSearchBar,
            'rounded-2xl': isAboveMD && roundedSearchBar,
          }
        )}
        hidden={isHidden}
      >
        {renderCounter()}
        {!roundedSearchBar && renderFooter()}
      </div>
      {isModalOpen && (
        <Modal
          title={t('component:guest_counter.modal.title')}
          onClose={() => setIsModalOpen(false)}
        >
          <div className="flex h-full flex-col justify-between">
            {renderCounter()}
            {renderFooter()}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default GuestCounter;
